import { useState, useEffect } from 'react';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import TopNavBar from '../components/TopNavBar';
import SideNavBar from '../components/SideNavBar';

import Refreshing from '../components/Refreshing';

import * as apiComms from '../apiComms';
import * as logger from '../logger';

import * as dayjs from 'dayjs'

import { IoMdSettings } from "react-icons/io";
import { FaTrashAlt } from "react-icons/fa";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import { IoMdAddCircleOutline } from "react-icons/io";

import PromotionAddOrEditModal from '../modals/PromotionAddOrEditModal';
import DeletePromotionItemModal from '../modals/DeletePromotionItemModal';

import ActivatableCouponCardComponent from '../components/ActivatableCouponCardComponent';
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

const PROG = 'NonValidatedCoupons.js';

const NonValidatedCoupons = (props) => {

  const [showInfoBox, setShowInfoBox] = useState(true);
  const [displayCoupons, setDisplayCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddCouponModalVisible, setIsAddCouponModalVisible] = useState(false);
  const [isAddOrEditCouponModalRefreshing, setIsAddOrEditCouponModalRefreshing] = useState(false);
  const [editCouponItem, setEditCouponItem] = useState(null);
  const [isEditCouponModalVisible, setIsEditCouponModalVisible] = useState(false);
  const [deleteCouponItem, setDeleteCouponItem] = useState(null);
  const [isDeleteCouponModalVisible, setIsDeleteCouponModalVisible] = useState(false);

  const [isInfoToastVisible, setIsInfoToastVisible ] = useState(false);
  const [infoToastMessage, setInfoToastMessage ] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    refreshCoupons();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // small drag threshold
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = displayCoupons.findIndex((item) => item.couponId === active.id);
      const newIndex = displayCoupons.findIndex((item) => item.couponId === over.id);
      const updatedRecords = arrayMove(displayCoupons, oldIndex, newIndex);
      setDisplayCoupons(arrayMove(displayCoupons, oldIndex, newIndex)); // Instant Update
      updateItemOrderInCloud(updatedRecords);
    }
  };

  const updateItemOrderInCloud = async (updatedRecords) => {
    const FUNCTION = 'updateItemOrderInCloud';

    // Show Info Toast
    setInfoToastMessage('Saving...');
    setIsInfoToastVisible(true);

    let updates = [];
    for (let i=0;i<updatedRecords.length;i++) {
      const coupon = updatedRecords[i];
      const updateRec = {
        couponId : coupon.couponId,
        orderId : i
      };
      updates.push(updateRec);
    }

    const res = await apiComms.updateNonValidatedCouponOrders(updates);
    logger.debug('updateNonValidatedCouponOrders response', { PROG, FUNCTION, res });

    if (res.isErr) {
      // An error occured
      setInfoToastMessage('Error Occured: '+res.errMsg);
      setTimeout(() => {
        setIsInfoToastVisible(false);
      }, 2000);
      // Revert the order changes
      setDisplayCoupons((prevDisplayCoupons) =>
        [...prevDisplayCoupons].sort((a, b) => {
          const a_orderId = a.orderId;
          const b_orderId = b.orderId;
          if (a_orderId < b_orderId) {
            return -1;
          }
          else if (a_orderId > b_orderId) {
            return 1;
          }
          else {
            return 0;
          }
        }
      ));
    }
    else {
      // Success
      setInfoToastMessage('Success: Updated !');
      setTimeout(() => {
        setIsInfoToastVisible(false);
      }, 2000);
      let updateOrderIds = {};
      for (let i=0;i<updatedRecords.length;i++) {
        updateOrderIds[updatedRecords[i].couponId] = i;
      }
      // Update displayPromotions Array
      displayCoupons.map((coupon) => {
        coupon.orderId = updateOrderIds[coupon.couponId];
      });
      // Resort the displayPromotions Array
      setDisplayCoupons((prevDisplayCoupons) =>
        [...prevDisplayCoupons].sort((a, b) => {
          const a_orderId = a.orderId;
          const b_orderId = b.orderId;
          if (a_orderId < b_orderId) {
            return -1;
          }
          else if (a_orderId > b_orderId) {
            return 1;
          }
          else {
            return 0;
          }
        }
      ));
    }
  };

  const refreshCoupons = async () => {
    const FUNCTION = 'refreshCoupons';
      
    setIsLoading(true);
  
    const couponsRes = await apiComms.getAllNonValidatedCoupons();
    if (couponsRes.isErr) {
      alert('Sorry an error occured: '+couponsRes.errMsg);
    }
    else {
      let coupons = couponsRes.response.nonvalidated_coupons;
      _enrichCoupons(coupons);

      // Sort coupons 09/Jan/2026
      coupons.sort((a, b) => {
        const a_orderId = a.orderId;
        const b_orderId = b.orderId;
        if (a_orderId < b_orderId) {
          return -1;
        }
        else if (a_orderId > b_orderId) {
          return 1;
        }
        else {
          return 0;
        }
      });  

      setDisplayCoupons(coupons);
    }
    setIsLoading(false);
  };

  const _enrichCoupons = (coupons) => {
    const FUNCTION = '_enrichCoupons';
    for (let i=0;i<coupons.length;i++) {
      let coupon = coupons[i];
      const displayFrom = dayjs((coupon.displayFrom * 1000));
      const displayTo = dayjs((coupon.displayTo * 1000));
      const now = dayjs();
      const isLive = (displayFrom.isBefore(now) && displayTo.isAfter(now));
      if (isLive) {
        coupon.enrichedData = { displayStatus : 'LIVE' };
      }
      else {
        coupon.enrichedData = { displayStatus : 'NOT LIVE' };
      }
    }
  };

  const handleCancelEditCouponModalPress = () => {
    setIsEditCouponModalVisible(false);
  };

  const handleCancelNewCouponModalPress = () => {
    setIsAddCouponModalVisible(false);
  };

  const handleUpdateCouponsEvent = () => {
    setIsAddCouponModalVisible(false);
    refreshCoupons();
  };

  const handleEditCouponItem = (coupon) => {
    console.log('Edit Coupon: '+JSON.stringify(coupon));
    setEditCouponItem(coupon);
    setIsEditCouponModalVisible(true);
  };

  const handleDeleteCouponItem = (coupon) => {
    setDeleteCouponItem(coupon);
    setIsDeleteCouponModalVisible(true);
  };

  const handleConfirmDeleteCouponItem = async (coupon) => {

    setIsDeleteCouponModalVisible(false);
    setIsLoading(true);

    const couponId = coupon.couponId;

    const res = await apiComms.deleteNonValidatedCoupon(couponId);
    

    setIsLoading(false);

    refreshCoupons();

  };

  const handleEditCoupon = async (coupon) => {
    const FUNCTION = 'handleEditCoupon';

    // Make the AddOrEdit Modal Show it's refreshing view
    setIsAddOrEditCouponModalRefreshing(true);

    const res = await apiComms.updateExistingNonValidatedCoupon(
      coupon.promotionId,
      coupon.title,
      coupon.linkUrl,
      coupon.imageUrl,
      coupon.displayFrom,
      coupon.displayTo
    );            
    console.log('Response from updateCoupon call: '+JSON.stringify(res));

    setIsAddOrEditCouponModalRefreshing(false);
    setIsEditCouponModalVisible(false);

    // Reload all our Coupon Items
    refreshCoupons();
  };
  
    
    

  const handleAddNewCoupon = async (coupon) => {
    const FUNCTION = 'handleAddNewCoupon';

    // Hide the PromotionAddOrEditModal Modal
    //setIsAddCouponModalVisible(false);

    // Make the AddOrEdit Modal Show it's refreshing view
    setIsAddOrEditCouponModalRefreshing(true);

    const res = await apiComms.addNewNonValidatedCoupon(
      coupon.title,
      coupon.linkUrl,
      coupon.imageUrl,
      coupon.displayFrom,
      coupon.displayTo
    );            
    console.log('Response from addNewNonValidatedCoupon call: '+JSON.stringify(res));

    setIsAddCouponModalVisible(false);
    setIsAddOrEditCouponModalRefreshing(false);
  
    // Reload all our Coupon Items
    refreshCoupons();
  };

  return (
    <>
      { isAddCouponModalVisible ? (
        <PromotionAddOrEditModal handleCancelPress = { handleCancelNewCouponModalPress } handleSubmitButtonPress = { handleAddNewCoupon } modalTitle='ADD NEW COUPON' modalKeyword='Coupon' isLinkUrl = { false } submitButtonText='Add New' isRefreshing = { isAddOrEditCouponModalRefreshing } />
      ) : null }
      { isEditCouponModalVisible ? (
        <PromotionAddOrEditModal handleCancelPress = { handleCancelEditCouponModalPress } handleSubmitButtonPress = { handleEditCoupon } modalTitle='EDIT COUPON' modalKeyword='Coupon' isLinkUrl = { false } submitButtonText='Update' isRefreshing = { isAddOrEditCouponModalRefreshing } existingPromotion = { editCouponItem } />
      ) : null }
      { isDeleteCouponModalVisible ? (
        <DeletePromotionItemModal promotion = { deleteCouponItem } modalTitle='DELETE COUPON' modalKeyword='Coupon' isLinkUrl = { false } handleCancelPress = { () => { setIsDeleteCouponModalVisible(false); }} handleDeleteButtonPress = { (coupon) => { handleConfirmDeleteCouponItem(coupon); }} />
      ) : null }

      <TopNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <SideNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <div style={{ marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT, marginRight : 10, marginTop : 0, paddingTop : 45 }}>
        <h1 style={{ fontSize: 18, fontWeight : 'bold', paddingTop : 20, marginLeft : 10 }}>Coupons & Coupons > Activatable Coupons</h1>

        { showInfoBox ? (
          <div style={{ marginTop : 20 }} className="alert alert-primary lert-dismissible fade show" role="alert">
            <h4 className="alert-heading">About Activatable Coupons</h4>
            <p>Customers can interact with these Coupon items that are displayed in a horizontal carousel on both the Home view and the V.I.P. Offers view in the Petstop App.  These highlight offers and discounts for Loyalty customers and can be pressed to "activate" them.</p>
            <button style={{ position : 'absolute', top : 10, right : 10 }} type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={ () => { setShowInfoBox(false); }} onPress={ () => { setShowInfoBox(false); }}></button>
          </div>
        ) : null }

        { isLoading ? (
          <Refreshing message='Loading Coupons...' />
        ) : null }

        { !isLoading ? (
          <>
            <div style={{ display : 'flex', flexWrap : 'wrap', height : '100%', width : '100%', flexDirection : 'row', justifyContent : 'flex-start', alignItems : 'center' }}>
              <div style={{ width : '100%' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={displayCoupons.map((item) => item.couponId)} strategy={rectSortingStrategy}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(256px, 1fr))",
                      gap: 5,
                      padding: 0,
                    }}
                  >
                    { displayCoupons.map((coupon, i) => {
                      const couponId = coupon.couponId;
                      return (
                        <ActivatableCouponCardComponent
                          key = { couponId }
                          id = { couponId }
                          coupon = { coupon }
                          handleEditCouponItem = { handleEditCouponItem }
                          handleDeleteCouponItem = { handleDeleteCouponItem }
                          totalItems = { displayCoupons.length }
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              </div>

              </div>
            <button
              type="button"
              className="btn btn-info position-fixed bottom-0 end-0 m-4 rounded-circle"
              style={{ width : 56, height : 56 }}
              onClick = { () => { setIsAddCouponModalVisible(true); }}
              onPress = { () => { setIsAddCouponModalVisible(true); }}
            >
              +
            </button>
          </>
        ) : null }

        
      </div>

      { isInfoToastVisible ? (
        <div className="toast-container position-fixed bottom-0 end-0 p-3">
          <div className="toast show text-bg-info text-white">
            <div className="d-flex">
              <div className="toast-body">
                { infoToastMessage }
              </div>
              <button type="button" className="btn-close me-2 m-auto bg-white" data-bs-dismiss="toast" aria-label="Close" onClick = { () => { setIsInfoToastVisible(false); }} onPress = { () => { setIsInfoToastVisible(false); }}></button>
            </div>
          </div>
        </div>
      ) : null }

    </>
  );

};

export default NonValidatedCoupons;
